import { currentYear } from "@/lib/siteYear";
// Master "hub" landing pages per product type, sitting at /{slug}.
// Each hub offers two clear CTAs: read reviews and read the buying guide.
// This consolidates review + buying-guide intent on a single canonical URL
// while keeping the existing /best-*-uk and /category/reviews?sub=... pages working.

export interface ProductHubConfig {
  slug: string;             // URL slug — also the route at /{slug}
  title: string;            // Plural product noun, e.g. "Lawn Mowers"
  singular: string;         // Singular noun, e.g. "lawn mower"
  intro: string;            // Short paragraph below H1
  metaTitle: string;
  metaDescription: string;
  /** URL of the buying guide page. /best-*-uk where one exists. */
  buyingGuideHref: string;
  /** URL of the reviews listing for this product. */
  reviewsHref: string;
  /** Article category names matched (case-insensitive) when listing latest articles. */
  articleCategories: string[];
  heroGradient: string;
}

const Y = currentYear();

export const productHubs: Record<string, ProductHubConfig> = {
  "lawn-mowers": {
    slug: "lawn-mowers",
    title: "Lawn Mowers",
    singular: "lawn mower",
    intro:
      "Find the right mower for your lawn — from tiny urban patches to half-acre plots. Read our independent reviews of UK models or jump straight to our buying guide for help choosing.",
    metaTitle: `Lawn Mowers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK lawn mower reviews and a complete buying guide. Petrol, electric, cordless, robotic and ride-on models compared and rated.",
    buyingGuideHref: "/best-lawn-mowers-uk",
    reviewsHref: "/category/reviews?sub=lawn-mowers",
    articleCategories: ["Lawn Mowers"],
    heroGradient: "bg-linear-to-br from-green-50 to-emerald-50",
  },
  "chainsaws": {
    slug: "chainsaws",
    title: "Chainsaws",
    singular: "chainsaw",
    intro:
      "From light pruning saws to heavy felling chainsaws, our independent reviews and buying guide help you pick the right tool — safely.",
    metaTitle: `Chainsaws ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Expert UK chainsaw reviews and a no-nonsense buying guide. Petrol, electric and cordless chainsaws compared, rated and ranked.",
    buyingGuideHref: "/best-chainsaws-uk",
    reviewsHref: "/category/reviews?sub=chainsaws",
    articleCategories: ["Chainsaws", "Chainsaw"],
    heroGradient: "bg-linear-to-br from-orange-50 to-red-50",
  },
  "generators": {
    slug: "generators",
    title: "Generators",
    singular: "generator",
    intro:
      "Backup power, off-grid use or trade work — our independent reviews and buying guide compare the UK's best portable, inverter and standby generators.",
    metaTitle: `Generators ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK generator reviews and a comprehensive buying guide. Inverter, portable and standby models tested, rated and compared.",
    buyingGuideHref: "/best-generators-uk",
    reviewsHref: "/category/reviews?sub=generators",
    articleCategories: ["Generators", "Generator"],
    heroGradient: "bg-linear-to-br from-yellow-50 to-amber-50",
  },
  "pressure-washers": {
    slug: "pressure-washers",
    title: "Pressure Washers",
    singular: "pressure washer",
    intro:
      "Patios, cars, driveways and decking — the right pressure washer transforms tired surfaces in minutes. Read our reviews or jump to the buying guide.",
    metaTitle: `Pressure Washers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Expert UK pressure washer reviews and a complete buying guide. Electric and petrol models from Karcher, Bosch, Nilfisk and more compared.",
    buyingGuideHref: "/best-pressure-washers-uk",
    reviewsHref: "/category/reviews?sub=pressure-washers",
    articleCategories: ["Pressure Washers"],
    heroGradient: "bg-linear-to-br from-blue-50 to-cyan-50",
  },
  "leaf-blowers": {
    slug: "leaf-blowers",
    title: "Leaf Blowers",
    singular: "leaf blower",
    intro:
      "Save hours of raking with the right leaf blower. Read our independent reviews or jump to the buying guide for help choosing.",
    metaTitle: `Leaf Blowers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK leaf blower reviews and a clear buying guide. Cordless, corded and petrol models compared and rated.",
    buyingGuideHref: "/best-leaf-blowers-uk",
    reviewsHref: "/category/reviews?sub=leaf-blowers",
    articleCategories: ["Leaf Blowers", "Leaf Blower"],
    heroGradient: "bg-linear-to-br from-orange-50 to-amber-50",
  },
  "hedge-trimmers": {
    slug: "hedge-trimmers",
    title: "Hedge Trimmers",
    singular: "hedge trimmer",
    intro:
      "From neat box hedges to long runs of leylandii, the right hedge trimmer turns a chore into an easy afternoon job. We test and compare cordless, corded electric, petrol and long-reach models — so you can pick the trimmer that suits your hedges, your budget and your back.",
    metaTitle: `Best Hedge Trimmers UK ${Y}: Cordless, Corded & Petrol Reviews`,
    metaDescription:
      "Independent UK hedge trimmer reviews and buying guide. Cordless, corded, petrol and long-reach models compared with pros, cons and prices for every garden.",
    buyingGuideHref: "/best-hedge-trimmers-uk",
    reviewsHref: "/category/reviews?sub=hedge-trimmers",
    articleCategories: ["Hedge Trimmers"],
    heroGradient: "bg-linear-to-br from-green-50 to-teal-50",
  },
  "strimmers": {
    slug: "strimmers",
    title: "Strimmers & Grass Trimmers",
    singular: "strimmer",
    intro:
      "Neat lawn edges, rough grass, brambles — our independent reviews and buying guide help you pick the right strimmer.",
    metaTitle: `Strimmers & Grass Trimmers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK strimmer and grass trimmer reviews. Cordless, corded and petrol brushcutters compared.",
    buyingGuideHref: "/best-grass-trimmers-brushcutters-uk",
    reviewsHref: "/category/reviews?sub=strimmers",
    articleCategories: ["Strimmers", "Grass Trimmers"],
    heroGradient: "bg-linear-to-br from-emerald-50 to-teal-50",
  },
  "tillers-cultivators": {
    slug: "tillers-cultivators",
    title: "Tillers & Cultivators",
    singular: "tiller",
    intro:
      "Prepare a veg bed in minutes instead of hours. Read our reviews or jump to the buying guide.",
    metaTitle: `Tillers & Cultivators ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Expert UK tiller and rotavator reviews and buying guide. Electric and petrol cultivators compared.",
    buyingGuideHref: "/best-tillers-rotavators-uk",
    reviewsHref: "/category/reviews?sub=tillers-cultivators",
    articleCategories: ["Tillers", "Cultivators", "Rotavators"],
    heroGradient: "bg-linear-to-br from-amber-50 to-stone-50",
  },
  "scarifiers": {
    slug: "scarifiers",
    title: "Lawn Scarifiers",
    singular: "scarifier",
    intro:
      "A scarifier is the single biggest improvement you can make to a tired UK lawn. Reviews and buying guide below.",
    metaTitle: `Lawn Scarifiers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK lawn scarifier reviews and buying guide. Electric and petrol scarifiers / aerators compared and rated.",
    buyingGuideHref: "/best-scarifiers-uk",
    reviewsHref: "/category/reviews?sub=scarifiers",
    articleCategories: ["Scarifiers"],
    heroGradient: "bg-linear-to-br from-lime-50 to-green-50",
  },
  "wood-chippers": {
    slug: "wood-chippers",
    title: "Wood Chippers",
    singular: "wood chipper",
    intro:
      "Turn prunings and brash into useful mulch. Independent reviews and a clear buying guide below.",
    metaTitle: `Wood Chippers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Expert UK wood chipper reviews and buying guide. Petrol and electric models compared.",
    buyingGuideHref: "/best-wood-chippers-uk",
    reviewsHref: "/category/reviews?sub=wood-chippers",
    articleCategories: ["Wood Chippers"],
    heroGradient: "bg-linear-to-br from-stone-50 to-amber-50",
  },
  "shredders": {
    slug: "shredders",
    title: "Garden Shredders",
    singular: "garden shredder",
    intro:
      "Keep your prunings out of the tip and turn them into mulch with a quality shredder.",
    metaTitle: `Garden Shredders ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK garden shredder reviews and a clear buying guide. Impact and silent roller models compared.",
    buyingGuideHref: "/best-shredders-uk",
    reviewsHref: "/category/reviews?sub=shredders",
    articleCategories: ["Shredders"],
    heroGradient: "bg-linear-to-br from-green-50 to-lime-50",
  },
  "pole-saws": {
    slug: "pole-saws",
    title: "Pole Saws & Pruners",
    singular: "pole saw",
    intro:
      "Reach high branches safely from the ground with a quality pole saw or long-reach pruner. Our independent UK reviews and buying guide compare cordless, petrol and electric models for weight, reach and cutting power.",
    metaTitle: `Pole Saws & Pruners ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK pole saw and long-reach pruner reviews and a complete buying guide. Cordless, petrol and electric telescopic chainsaws compared on reach, weight and bar length.",
    buyingGuideHref: "/best-pole-saws-uk",
    reviewsHref: "/category/reviews?sub=pole-saws",
    articleCategories: ["Pole Saws", "Pole Saw", "Pole Pruners", "Long Reach Pruners"],
    heroGradient: "bg-linear-to-br from-green-50 to-emerald-50",
  },

  "log-splitters": {
    slug: "log-splitters",
    title: "Log Splitters",
    singular: "log splitter",
    intro:
      "A log splitter can turn a tiring pile of rounds into usable firewood with far less effort than an axe. Our UK guides explain how electric, petrol and manual splitters differ, what splitting force suits your timber, and which specifications matter. Start with the buying advice, then compare our model reviews before choosing.",
    metaTitle: "Log Splitters 2026 | UK Reviews & Buying Guide",
    metaDescription:
      "Expert UK log splitter reviews and a complete buying guide. Electric and hydraulic models compared.",
    buyingGuideHref: "/log-splitters/buying-guides",
    reviewsHref: "/log-splitters/reviews",
    articleCategories: ["Log Splitters"],
    heroGradient: "bg-linear-to-br from-stone-50 to-amber-50",
  },
  "trenchers": {
    slug: "trenchers",
    title: "Trenchers",
    singular: "trencher",
    intro:
      "Cable runs, drainage and irrigation — a powered trencher turns a week of digging into an afternoon.",
    metaTitle: `Trenchers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK trencher reviews and a clear buying guide for landscapers and DIYers.",
    buyingGuideHref: "/best-trenchers-uk",
    reviewsHref: "/category/reviews?sub=trenchers",
    articleCategories: ["Trenchers"],
    heroGradient: "bg-linear-to-br from-stone-50 to-zinc-50",
  },
  "stump-grinders": {
    slug: "stump-grinders",
    title: "Stump Grinders",
    singular: "stump grinder",
    intro:
      "Remove stubborn tree stumps quickly and safely with the right grinder. Our independent reviews and buying guide compare petrol and PTO stump grinders for power, cutting depth and ease of use.",
    metaTitle: `Stump Grinders ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK stump grinder reviews and a complete buying guide. Petrol and PTO stump grinders compared for cutting depth, power and manoeuvrability.",
    buyingGuideHref: "/best-stump-grinders-uk",
    reviewsHref: "/category/reviews?sub=stump-grinders",
    articleCategories: ["Stump Grinders", "Stump Grinder"],
    heroGradient: "bg-linear-to-br from-stone-50 to-amber-50",
  },
  "cement-mixers": {
    slug: "cement-mixers",
    title: "Cement Mixers",
    singular: "cement mixer",
    intro:
      "From a small wall to a full landscaping project, a quality cement mixer saves your back and gives a far better mix than hand-mixing.",
    metaTitle: `Cement Mixers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Expert UK cement mixer reviews and a complete buying guide. Electric and petrol mixers from JCB, Altrad Belle, Hyundai and more.",
    buyingGuideHref: "/best-cement-mixers-uk",
    reviewsHref: "/category/reviews?sub=cement-mixers",
    articleCategories: ["Cement Mixers"],
    heroGradient: "bg-linear-to-br from-gray-50 to-stone-50",
  },
  "air-compressors": {
    slug: "air-compressors",
    title: "Air Compressors",
    singular: "air compressor",
    intro:
      "From inflating tyres to running spray guns and nailers, an air compressor is one of the most useful tools in any workshop.",
    metaTitle: `Air Compressors ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK air compressor reviews and a clear buying guide. Portable and stationary models compared.",
    buyingGuideHref: "/best-air-compressors-uk",
    reviewsHref: "/category/reviews?sub=air-compressors",
    articleCategories: ["Air Compressors"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "compactor-plates": {
    slug: "compactor-plates",
    title: "Compactor Plates",
    singular: "compactor plate",
    intro:
      "Compact sub-bases for patios, driveways and block paving the right way. Independent UK compactor plate reviews and a clear buying guide below.",
    metaTitle: `Compactor Plates ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK compactor plate (wacker plate) reviews and a complete buying guide. Petrol and electric models compared on weight, plate size and compaction force.",
    buyingGuideHref: "/best-compactor-plates-uk",
    reviewsHref: "/category/reviews?sub=compactor-plates",
    articleCategories: ["Compactor Plates", "Wacker Plates"],
    heroGradient: "bg-linear-to-br from-stone-50 to-zinc-50",
  },
  "air-fans-ventilators": {
    slug: "air-fans-ventilators",
    title: "Air Fans & Ventilators",
    singular: "air fan",
    intro:
      "Keep workshops, garages and worksites cool, dry and dust-free with the right air fan or ventilator. Independent UK reviews and a clear buying guide below.",
    metaTitle: `Air Fans & Ventilators ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK air fan and ventilator reviews and a complete buying guide. Drum fans, axial blowers and extractor fans compared on airflow, noise and build quality.",
    buyingGuideHref: "/best-air-fans-ventilators-uk",
    reviewsHref: "/category/reviews?sub=air-fans-ventilators",
    articleCategories: ["Air Fans", "Ventilators", "Air Fans & Ventilators"],
    heroGradient: "bg-linear-to-br from-sky-50 to-slate-50",
  },
  "air-conditioning-coolers": {
    slug: "air-conditioning-coolers",
    title: "Air Conditioning & Coolers",
    singular: "air conditioner",
    intro:
      "Stay cool through UK heatwaves at home, in the office or in the workshop. Independent reviews and a clear buying guide for portable air conditioners, evaporative coolers and tower fans below.",
    metaTitle: `Air Conditioning & Coolers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK air conditioner and cooler reviews and a complete buying guide. Portable AC units, evaporative (swamp) coolers and tower coolers compared on BTU, noise and running cost.",
    buyingGuideHref: "/best-air-conditioners-uk",
    reviewsHref: "/category/reviews?sub=air-conditioning-coolers",
    articleCategories: ["Air Conditioning", "Air Conditioners", "Coolers", "Evaporative Coolers", "Portable Air Conditioners"],
    heroGradient: "bg-linear-to-br from-sky-50 to-cyan-50",
  },
  "impact-drivers": {
    slug: "impact-drivers",
    title: "Impact Drivers",
    singular: "impact driver",
    intro:
      "From flat-pack assembly to deck building, the right impact driver speeds up every fixing job. Independent UK reviews and a clear buying guide below.",
    metaTitle: `Impact Drivers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK impact driver reviews and a complete buying guide. Cordless 12V, 18V and 36V brushless models from DeWalt, Makita, Milwaukee, Bosch and more compared.",
    buyingGuideHref: "/best-impact-drivers-uk",
    reviewsHref: "/category/reviews?sub=impact-drivers",
    articleCategories: ["Impact Drivers", "Impact Driver"],
    heroGradient: "bg-linear-to-br from-yellow-50 to-orange-50",
  },
  "water-pumps": {
    slug: "water-pumps",
    title: "Water Pumps",
    singular: "water pump",
    intro:
      "Drain flooded areas, empty ponds or transfer water across your property with the right pump. Independent UK water pump reviews and a clear buying guide below.",
    metaTitle: `Water Pumps ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK water pump reviews and a complete buying guide. Submersible, dirty-water and clean-water pumps compared for drainage, irrigation and transfer tasks.",
    buyingGuideHref: "/best-water-pumps-uk",
    reviewsHref: "/category/reviews?sub=water-pumps",
    articleCategories: ["Water Pumps", "Water Pump"],
    heroGradient: "bg-linear-to-br from-sky-50 to-blue-50",
  },
  "mini-dumpers": {
    slug: "mini-dumpers",
    title: "Mini Dumpers",
    singular: "mini dumper",
    intro:
      "Move soil, rubble, logs and aggregates around site without breaking your back. Independent UK mini dumper reviews and a clear buying guide below.",
    metaTitle: `Mini Dumpers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK mini dumper reviews and a complete buying guide. Tracked and wheeled power barrows compared on payload, tip type and terrain.",
    buyingGuideHref: "/best-mini-dumpers-uk",
    reviewsHref: "/category/reviews?sub=mini-dumpers",
    articleCategories: ["Mini Dumpers", "Power Barrows"],
    heroGradient: "bg-linear-to-br from-stone-50 to-zinc-50",
  },
  "earth-augers": {
    slug: "earth-augers",
    title: "Earth Augers",
    singular: "earth auger",
    intro:
      "Plant trees, sink fence posts and dig footings in a fraction of the time. Independent UK earth auger reviews and a clear buying guide below.",
    metaTitle: `Earth Augers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK earth auger reviews and a complete buying guide. Petrol and cordless post hole diggers compared on power, bit size and ease of use.",
    buyingGuideHref: "/best-earth-augers-uk",
    reviewsHref: "/category/reviews?sub=earth-augers",
    articleCategories: ["Earth Augers", "Post Hole Diggers"],
    heroGradient: "bg-linear-to-br from-amber-50 to-stone-50",
  },
  "welders": {
    slug: "welders",
    title: "Welders",
    singular: "welder",
    intro:
      "Hobby fabrication, repairs or trade work — pick the right welder for the job. Independent UK welder reviews and a clear buying guide below.",
    metaTitle: `Welders ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK welder reviews and a complete buying guide. MIG, TIG, MMA stick and multi-process welders compared on duty cycle, amperage and ease of use.",
    buyingGuideHref: "/best-welders-uk",
    reviewsHref: "/category/reviews?sub=welders",
    articleCategories: ["Welders", "Welding"],
    heroGradient: "bg-linear-to-br from-orange-50 to-red-50",
  },
  "water-butts": {
    slug: "water-butts",
    title: "Water Butts",
    singular: "water butt",
    intro:
      "Cut your water bill and keep the garden green through dry spells. Independent UK water butt reviews and a clear buying guide below.",
    metaTitle: `Water Butts ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK water butt reviews and a complete buying guide. Slimline, decorative and large-capacity rainwater tanks compared on capacity, materials and fittings.",
    buyingGuideHref: "/best-water-butts-uk",
    reviewsHref: "/category/reviews?sub=water-butts",
    articleCategories: ["Water Butts", "Rainwater Harvesting"],
    heroGradient: "bg-linear-to-br from-sky-50 to-blue-50",
  },
  "tool-chests": {
    slug: "tool-chests",
    title: "Tool Chests",
    singular: "tool chest",
    intro:
      "Keep your garden and workshop tools organised, protected and easy to find with the right tool chest or cabinet. Independent UK reviews and a clear buying guide below.",
    metaTitle: `Tool Chests ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK tool chest and cabinet reviews with a complete buying guide. Roller cabinets, top chests and combo sets compared on storage, security and value.",
    buyingGuideHref: "/best-tool-chests-uk",
    reviewsHref: "/category/reviews?sub=tool-chests",
    articleCategories: ["Tool Chests", "Tool Storage"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "greenhouses": {
    slug: "greenhouses",
    title: "Greenhouses",
    singular: "greenhouse",
    intro:
      "Extend your growing season and protect tender plants with the right greenhouse. Read our independent reviews or jump to the buying guide for help choosing.",
    metaTitle: `Greenhouses ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK greenhouse reviews and a complete buying guide. Polycarbonate, glass, lean-to and walk-in models compared for size, ventilation and value.",
    buyingGuideHref: "/best-greenhouses-uk",
    reviewsHref: "/category/reviews?sub=greenhouses",
    articleCategories: ["Greenhouses", "Greenhouse"],
    heroGradient: "bg-linear-to-br from-green-50 to-teal-50",
  },
  "seeds": {
    slug: "seeds",
    title: "Seeds",
    singular: "seed",
    intro:
      "Grow your best garden yet with the right seeds for every season. From veg patches to wildflower meadows, our buying guides and reviews help you choose.",
    metaTitle: `Seeds ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Expert UK seed reviews and a complete buying guide. Vegetable, flower, grass and wildflower seeds compared for germination, value and reliability.",
    buyingGuideHref: "/best-seeds-uk",
    reviewsHref: "/category/reviews?sub=seeds",
    articleCategories: ["Seeds", "Seed"],
    heroGradient: "bg-linear-to-br from-green-50 to-emerald-50",
  },
  "vegetables": {
    slug: "vegetables",
    title: "Vegetables",
    singular: "vegetable",
    intro:
      "From seed to harvest, get the most out of your vegetable patch. Our buying guides, variety comparisons and growing advice help UK gardeners grow better crops.",
    metaTitle: `Vegetables ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Expert UK vegetable growing guides and variety reviews. Seed potatoes, onion sets, veg plants and grow-your-own kits compared for taste, yield and reliability.",
    buyingGuideHref: "/best-vegetables-uk",
    reviewsHref: "/category/reviews?sub=vegetables",
    articleCategories: ["Vegetables", "Vegetable"],
    heroGradient: "bg-linear-to-br from-green-50 to-lime-50",
  },
  "fruits": {
    slug: "fruits",
    title: "Fruits",
    singular: "fruit",
    intro:
      "Grow your own fruit trees, bushes and canes for the sweetest harvest. Our buying guides and variety reviews help UK gardeners pick the best apples, berries and more.",
    metaTitle: `Fruits ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Expert UK fruit growing guides and variety reviews. Apple trees, berry bushes, strawberry plants and fruit canes compared for flavour, yield and ease of growing.",
    buyingGuideHref: "/best-fruits-uk",
    reviewsHref: "/category/reviews?sub=fruits",
    articleCategories: ["Fruits", "Fruit"],
    heroGradient: "bg-linear-to-br from-red-50 to-orange-50",
  },
  "garden-plants": {
    slug: "garden-plants",
    title: "Garden Plants",
    singular: "garden plant",
    intro:
      "From bedding annuals to established perennials, find the right plants for every border, bed and container. Our reviews and buying guides help UK gardeners choose with confidence.",
    metaTitle: `Garden Plants ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Expert UK garden plant reviews and buying guides. Perennials, annuals, shrubs and bedding plants compared for colour, hardiness and garden performance.",
    buyingGuideHref: "/best-garden-plants-uk",
    reviewsHref: "/category/reviews?sub=garden-plants",
    articleCategories: ["Garden Plants", "Plants"],
    heroGradient: "bg-linear-to-br from-green-50 to-teal-50",
  },
  "patio-heaters": {
    slug: "patio-heaters",
    title: "Patio Heaters",
    singular: "patio heater",
    intro:
      "Stretch the outdoor season into autumn and beyond. Independent UK patio heater reviews and a clear buying guide covering gas, electric and chiminea-style models.",
    metaTitle: `Patio Heaters ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK patio heater reviews and a complete buying guide. Gas, electric and freestanding patio heaters compared on heat output, running costs and safety.",
    buyingGuideHref: "/best-patio-heaters-uk",
    reviewsHref: "/category/reviews?sub=patio-heaters",
    articleCategories: ["Patio Heaters", "Patio Heater"],
    heroGradient: "bg-linear-to-br from-orange-50 to-amber-50",
  },
  "compost": {
    slug: "compost",
    title: "Compost",
    singular: "compost",
    intro:
      "The foundation of every thriving garden. Our reviews and buying guides compare peat-free, organic and specialist composts to help you give your plants the best start.",
    metaTitle: `Compost ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Expert UK compost reviews and a complete buying guide. Peat-free, organic, potting and specialist composts compared for structure, nutrition and sustainability.",
    buyingGuideHref: "/best-compost-uk",
    reviewsHref: "/category/reviews?sub=compost",
    articleCategories: ["Compost", "Soil"],
    heroGradient: "bg-linear-to-br from-amber-50 to-stone-50",
  },
  "space-heaters": {
    slug: "space-heaters",
    title: "Space Heaters",
    singular: "space heater",
    intro:
      "Warm up workshops, greenhouses, conservatories and home offices without cranking the central heating. Independent UK space heater reviews and a clear buying guide below.",
    metaTitle: `Space Heaters ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK space heater reviews and a complete buying guide. Electric, propane, kerosene and infrared heaters compared on heat output, running costs and safety.",
    buyingGuideHref: "/best-space-heaters-uk",
    reviewsHref: "/category/reviews?sub=space-heaters",
    articleCategories: ["Space Heaters", "Space Heater"],
    heroGradient: "bg-linear-to-br from-orange-50 to-red-50",
  },
  "rattan-furniture": {
    slug: "rattan-furniture",
    title: "Rattan Furniture",
    singular: "rattan furniture",
    intro:
      "Transform your patio or conservatory with quality rattan garden furniture. Our independent reviews and buying guide compare sofa sets, dining sets and daybeds for comfort, weather resistance and value.",
    metaTitle: `Rattan Furniture ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK rattan furniture reviews and a complete buying guide. Garden sofa sets, dining sets, daybeds and cube sets compared for weather resistance, comfort and value.",
    buyingGuideHref: "/best-rattan-furniture-uk",
    reviewsHref: "/category/reviews?sub=rattan-furniture",
    articleCategories: ["Rattan Furniture"],
    heroGradient: "bg-linear-to-br from-amber-50 to-stone-50",
  },
  "fire-pits": {
    slug: "fire-pits",
    title: "Fire Pits",
    singular: "fire pit",
    intro:
      "Create a warm focal point for evening gatherings with the right fire pit. Our independent reviews and buying guide compare wood-burning, gas and smokeless models for heat output, safety and style.",
    metaTitle: `Fire Pits ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK fire pit reviews and a complete buying guide. Wood-burning, gas and smokeless fire pits compared for heat output, portability, safety and value.",
    buyingGuideHref: "/best-fire-pits-uk",
    reviewsHref: "/category/reviews?sub=fire-pits",
    articleCategories: ["Fire Pits", "Fire Pit"],
    heroGradient: "bg-linear-to-br from-orange-50 to-red-50",
  },
  "hand-tools": {
    slug: "hand-tools",
    title: "Hand Tools",
    singular: "hand tool",
    intro:
      "Quality hand tools make every gardening job easier and more enjoyable. Our independent reviews and buying guide cover the best secateurs, spades, trowels and loppers for UK gardeners.",
    metaTitle: `Hand Tools ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK garden hand tool reviews and a complete buying guide. Secateurs, spades, forks, trowels and loppers compared for comfort, durability and value.",
    buyingGuideHref: "/best-hand-tools-uk",
    reviewsHref: "/category/reviews?sub=hand-tools",
    articleCategories: ["Hand Tools", "Hand Tool"],
    heroGradient: "bg-linear-to-br from-stone-50 to-zinc-50",
  },
  "patio-sets": {
    slug: "patio-sets",
    title: "Patio Sets",
    singular: "patio set",
    intro:
      "Find the perfect outdoor dining set for your garden or patio. Our independent reviews and buying guide compare rattan, metal and wooden patio sets for comfort, durability and value.",
    metaTitle: `Patio Sets ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK patio set reviews and a complete buying guide. Rattan, metal and wooden garden dining sets compared for seating capacity, weather resistance and value.",
    buyingGuideHref: "/best-patio-sets-uk",
    reviewsHref: "/category/reviews?sub=patio-sets",
    articleCategories: ["Patio Sets", "Garden Furniture Sets"],
    heroGradient: "bg-linear-to-br from-sky-50 to-blue-50",
  },
  "garden-chairs": {
    slug: "garden-chairs",
    title: "Garden Chairs",
    singular: "garden chair",
    intro:
      "Relax in style with the right garden chair. Our independent reviews and buying guide compare folding, recliner, hanging and dining chairs for comfort, durability and value.",
    metaTitle: `Garden Chairs ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK garden chair reviews and a complete buying guide. Folding, recliner, hanging egg and dining chairs compared for comfort, materials and value.",
    buyingGuideHref: "/best-garden-chairs-uk",
    reviewsHref: "/category/reviews?sub=garden-chairs",
    articleCategories: ["Garden Chairs", "Outdoor Chairs"],
    heroGradient: "bg-linear-to-br from-green-50 to-emerald-50",
  },
  "secateurs": {
    slug: "secateurs",
    title: "Secateurs",
    singular: "secateur",
    intro:
      "Clean, precise cuts make healthier plants. Our independent reviews and buying guide compare bypass, anvil and ratchet secateurs for cutting performance, comfort and durability.",
    metaTitle: `Secateurs ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK secateur reviews and a complete buying guide. Bypass, anvil and ratchet pruning shears compared for cutting capacity, comfort and value.",
    buyingGuideHref: "/best-secateurs-uk",
    reviewsHref: "/category/reviews?sub=secateurs",
    articleCategories: ["Secateurs", "Pruning Shears"],
    heroGradient: "bg-linear-to-br from-green-50 to-lime-50",
  },
  "spades-forks": {
    slug: "spades-forks",
    title: "Spades & Forks",
    singular: "spade or fork",
    intro:
      "The right digging tool saves your back and gets the job done faster. Our independent reviews and buying guide compare garden spades, forks and border spades for strength, comfort and value.",
    metaTitle: `Spades & Forks ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK garden spade and fork reviews and a complete buying guide. Digging spades, border spades, garden forks and digging forks compared for strength, comfort and value.",
    buyingGuideHref: "/best-spades-forks-uk",
    reviewsHref: "/category/reviews?sub=spades-forks",
    articleCategories: ["Spades & Forks", "Garden Spades", "Garden Forks"],
    heroGradient: "bg-linear-to-br from-stone-50 to-amber-50",
  },
  "trowels": {
    slug: "trowels",
    title: "Trowels",
    singular: "trowel",
    intro:
      "A quality trowel is essential for planting, transplanting and container gardening. Our independent reviews and buying guide compare hand trowels for blade strength, ergonomics and value.",
    metaTitle: `Trowels ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK garden trowel reviews and a complete buying guide. Hand trowels, transplanting trowels and planting trowels compared for blade quality, comfort and value.",
    buyingGuideHref: "/best-trowels-uk",
    reviewsHref: "/category/reviews?sub=trowels",
    articleCategories: ["Trowels", "Hand Trowels", "Garden Trowels"],
    heroGradient: "bg-linear-to-br from-amber-50 to-yellow-50",
  },
  "loppers": {
    slug: "loppers",
    title: "Loppers",
    singular: "lopper",
    intro:
      "Cut thicker branches with ease using the right lopper. Our independent reviews and buying guide compare bypass, anvil and telescopic loppers for cutting power, reach and comfort.",
    metaTitle: `Loppers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK lopper reviews and a complete buying guide. Bypass, anvil and telescopic tree loppers compared for cutting capacity, reach, comfort and value.",
    buyingGuideHref: "/best-loppers-uk",
    reviewsHref: "/category/reviews?sub=loppers",
    articleCategories: ["Loppers", "Lopping Shears", "Tree Loppers"],
    heroGradient: "bg-linear-to-br from-green-50 to-teal-50",
  },
  "carts-trailers": {
    slug: "carts-trailers",
    title: "Carts & Trailers",
    singular: "cart or trailer",
    intro:
      "Move soil, logs, tools and plants around your garden or allotment with ease. Our independent reviews and buying guide compare garden carts, trolleys and trailers for capacity, terrain handling and value.",
    metaTitle: `Carts & Trailers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK garden cart and trailer reviews with a complete buying guide. Dump carts, folding trolleys and tow-behind trailers compared for load capacity, terrain and value.",
    buyingGuideHref: "/best-carts-trailers-uk",
    reviewsHref: "/category/reviews?sub=carts-trailers",
    articleCategories: ["Carts & Trailers", "Garden Carts", "Garden Trolleys", "Trailers"],
    heroGradient: "bg-linear-to-br from-stone-50 to-amber-50",
  },
  "wood-burning-stoves": {
    slug: "wood-burning-stoves",
    title: "Wood Burning Stoves",
    singular: "wood burning stove",
    intro:
      "Warm your home or garden room with a beautiful and efficient wood burner. Our independent reviews and buying guide compare cast iron and steel models for heat output, efficiency and style.",
    metaTitle: `Wood Burning Stoves ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK wood burning stove reviews and a complete buying guide. Cast iron and steel log burners compared for heat output, efficiency, emissions and value.",
    buyingGuideHref: "/best-wood-burning-stoves-uk",
    reviewsHref: "/category/reviews?sub=wood-burning-stoves",
    articleCategories: ["Wood Burning Stoves", "Log Burners", "Wood Burners"],
    heroGradient: "bg-linear-to-br from-orange-50 to-red-50",
  },
  "cordless-drills": {
    slug: "cordless-drills",
    title: "Cordless Drills & Drill Drivers",
    singular: "cordless drill",
    intro:
      "Independent UK reviews and a clear buying guide for cordless drills & drill drivers — compare top brands on power, build quality and value.",
    metaTitle: `Cordless Drills & Drill Drivers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK cordless drills & drill drivers reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-cordless-drills-uk",
    reviewsHref: "/category/reviews?sub=cordless-drills",
    articleCategories: ["Cordless Drills", "Cordless Drills & Drill Drivers"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "impact-wrenches": {
    slug: "impact-wrenches",
    title: "Impact Wrenches",
    singular: "impact wrench",
    intro:
      "Independent UK reviews and a clear buying guide for impact wrenches — compare top brands on power, build quality and value.",
    metaTitle: `Impact Wrenches ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK impact wrenches reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-impact-wrenches-uk",
    reviewsHref: "/category/reviews?sub=impact-wrenches",
    articleCategories: ["Impact Wrenches", "Impact Wrenches"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "battery-ratchets": {
    slug: "battery-ratchets",
    title: "Battery Powered Ratchets",
    singular: "battery ratchet",
    intro:
      "Independent UK reviews and a clear buying guide for battery powered ratchets — compare top brands on power, build quality and value.",
    metaTitle: `Battery Powered Ratchets ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK battery powered ratchets reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-battery-ratchets-uk",
    reviewsHref: "/category/reviews?sub=battery-ratchets",
    articleCategories: ["Battery Ratchets", "Battery Powered Ratchets"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "mains-power-drills": {
    slug: "mains-power-drills",
    title: "Mains Power Drills (Non SDS)",
    singular: "mains drill",
    intro:
      "Independent UK reviews and a clear buying guide for mains power drills (non sds) — compare top brands on power, build quality and value.",
    metaTitle: `Mains Power Drills (Non SDS) ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK mains power drills (non sds) reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-mains-power-drills-uk",
    reviewsHref: "/category/reviews?sub=mains-power-drills",
    articleCategories: ["Mains Drills", "Mains Power Drills (Non SDS)"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "mains-sds-drills": {
    slug: "mains-sds-drills",
    title: "Mains Powered SDS, SDS+ and SDS MAX Drills",
    singular: "SDS drill",
    intro:
      "Independent UK reviews and a clear buying guide for mains powered sds, sds+ and sds max drills — compare top brands on power, build quality and value.",
    metaTitle: `Mains Powered SDS, SDS+ and SDS MAX Drills ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK mains powered sds, sds+ and sds max drills reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-mains-sds-drills-uk",
    reviewsHref: "/category/reviews?sub=mains-sds-drills",
    articleCategories: ["SDS Drills", "Mains Powered SDS, SDS+ and SDS MAX Drills"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "cordless-sds-drills": {
    slug: "cordless-sds-drills",
    title: "Cordless SDS & SDS+ Drills",
    singular: "cordless SDS drill",
    intro:
      "Independent UK reviews and a clear buying guide for cordless sds & sds+ drills — compare top brands on power, build quality and value.",
    metaTitle: `Cordless SDS & SDS+ Drills ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK cordless sds & sds+ drills reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-cordless-sds-drills-uk",
    reviewsHref: "/category/reviews?sub=cordless-sds-drills",
    articleCategories: ["Cordless SDS Drills", "Cordless SDS & SDS+ Drills"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "cordless-screwdrivers": {
    slug: "cordless-screwdrivers",
    title: "Cordless Screwdrivers",
    singular: "cordless screwdriver",
    intro:
      "Independent UK reviews and a clear buying guide for cordless screwdrivers — compare top brands on power, build quality and value.",
    metaTitle: `Cordless Screwdrivers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK cordless screwdrivers reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-cordless-screwdrivers-uk",
    reviewsHref: "/category/reviews?sub=cordless-screwdrivers",
    articleCategories: ["Cordless Screwdrivers", "Cordless Screwdrivers"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "magnetic-drills": {
    slug: "magnetic-drills",
    title: "Magnetic Drilling Systems",
    singular: "magnetic drill",
    intro:
      "Independent UK reviews and a clear buying guide for magnetic drilling systems — compare top brands on power, build quality and value.",
    metaTitle: `Magnetic Drilling Systems ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK magnetic drilling systems reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-magnetic-drills-uk",
    reviewsHref: "/category/reviews?sub=magnetic-drills",
    articleCategories: ["Magnetic Drills", "Magnetic Drilling Systems"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "mains-screwdrivers": {
    slug: "mains-screwdrivers",
    title: "Mains Screwdrivers",
    singular: "mains screwdriver",
    intro:
      "Independent UK reviews and a clear buying guide for mains screwdrivers — compare top brands on power, build quality and value.",
    metaTitle: `Mains Screwdrivers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK mains screwdrivers reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-mains-screwdrivers-uk",
    reviewsHref: "/category/reviews?sub=mains-screwdrivers",
    articleCategories: ["Mains Screwdrivers", "Mains Screwdrivers"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "twin-packs-kits": {
    slug: "twin-packs-kits",
    title: "Twin Packs & Kits",
    singular: "twin pack kit",
    intro:
      "Independent UK reviews and a clear buying guide for twin packs & kits — compare top brands on power, build quality and value.",
    metaTitle: `Twin Packs & Kits ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK twin packs & kits reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-twin-packs-kits-uk",
    reviewsHref: "/category/reviews?sub=twin-packs-kits",
    articleCategories: ["Twin Packs", "Twin Packs & Kits"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "right-angle-drills": {
    slug: "right-angle-drills",
    title: "Right Angle Drills",
    singular: "right angle drill",
    intro:
      "Independent UK reviews and a clear buying guide for right angle drills — compare top brands on power, build quality and value.",
    metaTitle: `Right Angle Drills ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK right angle drills reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-right-angle-drills-uk",
    reviewsHref: "/category/reviews?sub=right-angle-drills",
    articleCategories: ["Right Angle Drills", "Right Angle Drills"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "demolition-hammers": {
    slug: "demolition-hammers",
    title: "Demolition Hammers & Breakers",
    singular: "demolition hammer",
    intro:
      "Independent UK reviews and a clear buying guide for demolition hammers & breakers — compare top brands on power, build quality and value.",
    metaTitle: `Demolition Hammers & Breakers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK demolition hammers & breakers reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-demolition-hammers-uk",
    reviewsHref: "/category/reviews?sub=demolition-hammers",
    articleCategories: ["Demolition Hammers", "Demolition Hammers & Breakers"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "drill-stands": {
    slug: "drill-stands",
    title: "Drill Stands & Accessories",
    singular: "drill stand",
    intro:
      "Independent UK reviews and a clear buying guide for drill stands & accessories — compare top brands on power, build quality and value.",
    metaTitle: `Drill Stands & Accessories ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK drill stands & accessories reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-drill-stands-uk",
    reviewsHref: "/category/reviews?sub=drill-stands",
    articleCategories: ["Drill Stands", "Drill Stands & Accessories"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "oscillating-multi-tools": {
    slug: "oscillating-multi-tools",
    title: "Oscillating Multi Tools",
    singular: "oscillating multi tool",
    intro:
      "Independent UK reviews and a clear buying guide for oscillating multi tools — compare top brands on power, build quality and value.",
    metaTitle: `Oscillating Multi Tools ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK oscillating multi tools reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-oscillating-multi-tools-uk",
    reviewsHref: "/category/reviews?sub=oscillating-multi-tools",
    articleCategories: ["Multi Tools", "Oscillating Multi Tools"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "multi-tool-blades": {
    slug: "multi-tool-blades",
    title: "Multi Tool Blades & Accessories",
    singular: "multi tool blade",
    intro:
      "Independent UK reviews and a clear buying guide for multi tool blades & accessories — compare top brands on power, build quality and value.",
    metaTitle: `Multi Tool Blades & Accessories ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK multi tool blades & accessories reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-multi-tool-blades-uk",
    reviewsHref: "/category/reviews?sub=multi-tool-blades",
    articleCategories: ["Multi Tool Blades", "Multi Tool Blades & Accessories"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "circular-saws": {
    slug: "circular-saws",
    title: "Hand Held Circular Saws",
    singular: "circular saw",
    intro:
      "Independent UK reviews and a clear buying guide for hand held circular saws — compare top brands on power, build quality and value.",
    metaTitle: `Hand Held Circular Saws ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK hand held circular saws reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-circular-saws-uk",
    reviewsHref: "/category/reviews?sub=circular-saws",
    articleCategories: ["Circular Saws", "Hand Held Circular Saws"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "circular-saw-blades": {
    slug: "circular-saw-blades",
    title: "Circular Saw Blades",
    singular: "circular saw blade",
    intro:
      "Independent UK reviews and a clear buying guide for circular saw blades — compare top brands on power, build quality and value.",
    metaTitle: `Circular Saw Blades ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK circular saw blades reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-circular-saw-blades-uk",
    reviewsHref: "/category/reviews?sub=circular-saw-blades",
    articleCategories: ["Circular Saw Blades", "Circular Saw Blades"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "cut-off-saws": {
    slug: "cut-off-saws",
    title: "Cut Off Saws",
    singular: "cut off saw",
    intro:
      "Independent UK reviews and a clear buying guide for cut off saws — compare top brands on power, build quality and value.",
    metaTitle: `Cut Off Saws ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK cut off saws reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-cut-off-saws-uk",
    reviewsHref: "/category/reviews?sub=cut-off-saws",
    articleCategories: ["Cut Off Saws", "Cut Off Saws"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "reciprocating-saws": {
    slug: "reciprocating-saws",
    title: "Reciprocating Saws",
    singular: "reciprocating saw",
    intro:
      "Independent UK reviews and a clear buying guide for reciprocating saws — compare top brands on power, build quality and value.",
    metaTitle: `Reciprocating Saws ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK reciprocating saws reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-reciprocating-saws-uk",
    reviewsHref: "/category/reviews?sub=reciprocating-saws",
    articleCategories: ["Reciprocating Saws", "Reciprocating Saws"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "jigsaws": {
    slug: "jigsaws",
    title: "Jigsaws",
    singular: "jigsaw",
    intro:
      "Independent UK reviews and a clear buying guide for jigsaws — compare top brands on power, build quality and value.",
    metaTitle: `Jigsaws ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK jigsaws reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-jigsaws-uk",
    reviewsHref: "/category/reviews?sub=jigsaws",
    articleCategories: ["Jigsaws", "Jigsaws"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "routers-laminate-trimmers": {
    slug: "routers-laminate-trimmers",
    title: "Routers & Laminate Trimmers",
    singular: "router",
    intro:
      "Independent UK reviews and a clear buying guide for routers & laminate trimmers — compare top brands on power, build quality and value.",
    metaTitle: `Routers & Laminate Trimmers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK routers & laminate trimmers reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-routers-laminate-trimmers-uk",
    reviewsHref: "/category/reviews?sub=routers-laminate-trimmers",
    articleCategories: ["Routers", "Routers & Laminate Trimmers"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "router-jigs-templates": {
    slug: "router-jigs-templates",
    title: "Router Jigs & Templates",
    singular: "router jig",
    intro:
      "Independent UK reviews and a clear buying guide for router jigs & templates — compare top brands on power, build quality and value.",
    metaTitle: `Router Jigs & Templates ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK router jigs & templates reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-router-jigs-templates-uk",
    reviewsHref: "/category/reviews?sub=router-jigs-templates",
    articleCategories: ["Router Jigs", "Router Jigs & Templates"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "router-bit-sets": {
    slug: "router-bit-sets",
    title: "Router Bit Sets",
    singular: "router bit set",
    intro:
      "Independent UK reviews and a clear buying guide for router bit sets — compare top brands on power, build quality and value.",
    metaTitle: `Router Bit Sets ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK router bit sets reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-router-bit-sets-uk",
    reviewsHref: "/category/reviews?sub=router-bit-sets",
    articleCategories: ["Router Bit Sets", "Router Bit Sets"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "router-bits": {
    slug: "router-bits",
    title: "Router Bits - Individual",
    singular: "router bit",
    intro:
      "Independent UK reviews and a clear buying guide for router bits - individual — compare top brands on power, build quality and value.",
    metaTitle: `Router Bits - Individual ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK router bits - individual reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-router-bits-uk",
    reviewsHref: "/category/reviews?sub=router-bits",
    articleCategories: ["Router Bits", "Router Bits - Individual"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "router-guide-bushes": {
    slug: "router-guide-bushes",
    title: "Router Guide Bushes & Bearings",
    singular: "router guide bush",
    intro:
      "Independent UK reviews and a clear buying guide for router guide bushes & bearings — compare top brands on power, build quality and value.",
    metaTitle: `Router Guide Bushes & Bearings ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK router guide bushes & bearings reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-router-guide-bushes-uk",
    reviewsHref: "/category/reviews?sub=router-guide-bushes",
    articleCategories: ["Router Guide Bushes", "Router Guide Bushes & Bearings"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "router-accessories": {
    slug: "router-accessories",
    title: "Router Accessories & Parts",
    singular: "router accessory",
    intro:
      "Independent UK reviews and a clear buying guide for router accessories & parts — compare top brands on power, build quality and value.",
    metaTitle: `Router Accessories & Parts ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK router accessories & parts reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-router-accessories-uk",
    reviewsHref: "/category/reviews?sub=router-accessories",
    articleCategories: ["Router Accessories", "Router Accessories & Parts"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "router-tables": {
    slug: "router-tables",
    title: "Router Tables",
    singular: "router table",
    intro:
      "Independent UK reviews and a clear buying guide for router tables — compare top brands on power, build quality and value.",
    metaTitle: `Router Tables ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK router tables reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-router-tables-uk",
    reviewsHref: "/category/reviews?sub=router-tables",
    articleCategories: ["Router Tables", "Router Tables"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "angle-grinders": {
    slug: "angle-grinders",
    title: "Angle Grinders",
    singular: "angle grinder",
    intro:
      "Independent UK reviews and a clear buying guide for angle grinders — compare top brands on power, build quality and value.",
    metaTitle: `Angle Grinders ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK angle grinders reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-angle-grinders-uk",
    reviewsHref: "/category/reviews?sub=angle-grinders",
    articleCategories: ["Angle Grinders", "Angle Grinders"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "angle-grinder-accessories": {
    slug: "angle-grinder-accessories",
    title: "Angle Grinder Accessories",
    singular: "angle grinder accessory",
    intro:
      "Independent UK reviews and a clear buying guide for angle grinder accessories — compare top brands on power, build quality and value.",
    metaTitle: `Angle Grinder Accessories ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK angle grinder accessories reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-angle-grinder-accessories-uk",
    reviewsHref: "/category/reviews?sub=angle-grinder-accessories",
    articleCategories: ["Angle Grinder Accessories", "Angle Grinder Accessories"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "power-planers": {
    slug: "power-planers",
    title: "Power Planers",
    singular: "power planer",
    intro:
      "Independent UK reviews and a clear buying guide for power planers — compare top brands on power, build quality and value.",
    metaTitle: `Power Planers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK power planers reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-power-planers-uk",
    reviewsHref: "/category/reviews?sub=power-planers",
    articleCategories: ["Power Planers", "Power Planers"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "orbital-sanders": {
    slug: "orbital-sanders",
    title: "Orbital Sanders",
    singular: "orbital sander",
    intro:
      "Independent UK reviews and a clear buying guide for orbital sanders — compare top brands on power, build quality and value.",
    metaTitle: `Orbital Sanders ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK orbital sanders reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-orbital-sanders-uk",
    reviewsHref: "/category/reviews?sub=orbital-sanders",
    articleCategories: ["Orbital Sanders", "Orbital Sanders"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "drywall-sanders": {
    slug: "drywall-sanders",
    title: "Drywall Sanders",
    singular: "drywall sander",
    intro:
      "Independent UK reviews and a clear buying guide for drywall sanders — compare top brands on power, build quality and value.",
    metaTitle: `Drywall Sanders ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK drywall sanders reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-drywall-sanders-uk",
    reviewsHref: "/category/reviews?sub=drywall-sanders",
    articleCategories: ["Drywall Sanders", "Drywall Sanders"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "polishers": {
    slug: "polishers",
    title: "Polishers",
    singular: "polisher",
    intro:
      "Independent UK reviews and a clear buying guide for polishers — compare top brands on power, build quality and value.",
    metaTitle: `Polishers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK polishers reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-polishers-uk",
    reviewsHref: "/category/reviews?sub=polishers",
    articleCategories: ["Polishers", "Polishers"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "belt-sanders": {
    slug: "belt-sanders",
    title: "Portable Belt Sanders",
    singular: "belt sander",
    intro:
      "Independent UK reviews and a clear buying guide for portable belt sanders — compare top brands on power, build quality and value.",
    metaTitle: `Portable Belt Sanders ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK portable belt sanders reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-belt-sanders-uk",
    reviewsHref: "/category/reviews?sub=belt-sanders",
    articleCategories: ["Belt Sanders", "Portable Belt Sanders"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "plunge-saws": {
    slug: "plunge-saws",
    title: "Plunge Saws",
    singular: "plunge saw",
    intro:
      "Independent UK reviews and a clear buying guide for plunge saws — compare top brands on power, build quality and value.",
    metaTitle: `Plunge Saws ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK plunge saws reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-plunge-saws-uk",
    reviewsHref: "/category/reviews?sub=plunge-saws",
    articleCategories: ["Plunge Saws", "Plunge Saws"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "saw-guide-rails": {
    slug: "saw-guide-rails",
    title: "Saw Guides and Rails",
    singular: "saw guide rail",
    intro:
      "Independent UK reviews and a clear buying guide for saw guides and rails — compare top brands on power, build quality and value.",
    metaTitle: `Saw Guides and Rails ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK saw guides and rails reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-saw-guide-rails-uk",
    reviewsHref: "/category/reviews?sub=saw-guide-rails",
    articleCategories: ["Saw Guide Rails", "Saw Guides and Rails"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "mini-rotary-tools": {
    slug: "mini-rotary-tools",
    title: "Mini Rotary Tools & Engravers",
    singular: "rotary tool",
    intro:
      "Independent UK reviews and a clear buying guide for mini rotary tools & engravers — compare top brands on power, build quality and value.",
    metaTitle: `Mini Rotary Tools & Engravers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK mini rotary tools & engravers reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-mini-rotary-tools-uk",
    reviewsHref: "/category/reviews?sub=mini-rotary-tools",
    articleCategories: ["Rotary Tools", "Mini Rotary Tools & Engravers"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "rotary-tool-accessories": {
    slug: "rotary-tool-accessories",
    title: "Rotary Tool and Engraver Accessories",
    singular: "rotary tool accessory",
    intro:
      "Independent UK reviews and a clear buying guide for rotary tool and engraver accessories — compare top brands on power, build quality and value.",
    metaTitle: `Rotary Tool and Engraver Accessories ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK rotary tool and engraver accessories reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-rotary-tool-accessories-uk",
    reviewsHref: "/category/reviews?sub=rotary-tool-accessories",
    articleCategories: ["Rotary Tool Accessories", "Rotary Tool and Engraver Accessories"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "nail-staple-guns": {
    slug: "nail-staple-guns",
    title: "Nail Guns and Staple Guns and Accessories",
    singular: "nail gun",
    intro:
      "Independent UK reviews and a clear buying guide for nail guns and staple guns and accessories — compare top brands on power, build quality and value.",
    metaTitle: `Nail Guns and Staple Guns and Accessories ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK nail guns and staple guns and accessories reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-nail-staple-guns-uk",
    reviewsHref: "/category/reviews?sub=nail-staple-guns",
    articleCategories: ["Nail Guns", "Nail Guns and Staple Guns and Accessories"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "biscuit-dowel-jointers": {
    slug: "biscuit-dowel-jointers",
    title: "Biscuit & Dowel Jointers",
    singular: "biscuit jointer",
    intro:
      "Independent UK reviews and a clear buying guide for biscuit & dowel jointers — compare top brands on power, build quality and value.",
    metaTitle: `Biscuit & Dowel Jointers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK biscuit & dowel jointers reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-biscuit-dowel-jointers-uk",
    reviewsHref: "/category/reviews?sub=biscuit-dowel-jointers",
    articleCategories: ["Biscuit Jointers", "Biscuit & Dowel Jointers"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "heat-guns": {
    slug: "heat-guns",
    title: "Heat Guns",
    singular: "heat gun",
    intro:
      "Independent UK reviews and a clear buying guide for heat guns — compare top brands on power, build quality and value.",
    metaTitle: `Heat Guns ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK heat guns reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-heat-guns-uk",
    reviewsHref: "/category/reviews?sub=heat-guns",
    articleCategories: ["Heat Guns", "Heat Guns"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "table-saws": {
    slug: "table-saws",
    title: "Table Saws",
    singular: "table saw",
    intro:
      "Independent UK reviews and a clear buying guide for table saws — compare top brands on power, build quality and value.",
    metaTitle: `Table Saws ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK table saws reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-table-saws-uk",
    reviewsHref: "/category/reviews?sub=table-saws",
    articleCategories: ["Table Saws", "Table Saws"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "glue-guns": {
    slug: "glue-guns",
    title: "Glue Guns & Glue Sticks",
    singular: "glue gun",
    intro:
      "Independent UK reviews and a clear buying guide for glue guns & glue sticks — compare top brands on power, build quality and value.",
    metaTitle: `Glue Guns & Glue Sticks ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK glue guns & glue sticks reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-glue-guns-uk",
    reviewsHref: "/category/reviews?sub=glue-guns",
    articleCategories: ["Glue Guns", "Glue Guns & Glue Sticks"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "power-tool-batteries": {
    slug: "power-tool-batteries",
    title: "Power Tool Batteries & Chargers",
    singular: "power tool battery",
    intro:
      "Independent UK reviews and a clear buying guide for power tool batteries & chargers — compare top brands on power, build quality and value.",
    metaTitle: `Power Tool Batteries & Chargers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK power tool batteries & chargers reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-power-tool-batteries-uk",
    reviewsHref: "/category/reviews?sub=power-tool-batteries",
    articleCategories: ["Power Tool Batteries", "Power Tool Batteries & Chargers"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "autofeed-screwdrivers": {
    slug: "autofeed-screwdrivers",
    title: "Autofeed Screwdrivers",
    singular: "autofeed screwdriver",
    intro:
      "Independent UK reviews and a clear buying guide for autofeed screwdrivers — compare top brands on power, build quality and value.",
    metaTitle: `Autofeed Screwdrivers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK autofeed screwdrivers reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-autofeed-screwdrivers-uk",
    reviewsHref: "/category/reviews?sub=autofeed-screwdrivers",
    articleCategories: ["Autofeed Screwdrivers", "Autofeed Screwdrivers"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "compound-mitre-saws": {
    slug: "compound-mitre-saws",
    title: "Compound Mitre Saws",
    singular: "mitre saw",
    intro:
      "Independent UK reviews and a clear buying guide for compound mitre saws — compare top brands on power, build quality and value.",
    metaTitle: `Compound Mitre Saws ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK compound mitre saws reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-compound-mitre-saws-uk",
    reviewsHref: "/category/reviews?sub=compound-mitre-saws",
    articleCategories: ["Mitre Saws", "Compound Mitre Saws"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "concrete-grinders": {
    slug: "concrete-grinders",
    title: "Concrete Grinders & Cutters",
    singular: "concrete grinder",
    intro:
      "Independent UK reviews and a clear buying guide for concrete grinders & cutters — compare top brands on power, build quality and value.",
    metaTitle: `Concrete Grinders & Cutters ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK concrete grinders & cutters reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-concrete-grinders-uk",
    reviewsHref: "/category/reviews?sub=concrete-grinders",
    articleCategories: ["Concrete Grinders", "Concrete Grinders & Cutters"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "electric-die-grinders": {
    slug: "electric-die-grinders",
    title: "Electric Die Grinders",
    singular: "die grinder",
    intro:
      "Independent UK reviews and a clear buying guide for electric die grinders — compare top brands on power, build quality and value.",
    metaTitle: `Electric Die Grinders ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK electric die grinders reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-electric-die-grinders-uk",
    reviewsHref: "/category/reviews?sub=electric-die-grinders",
    articleCategories: ["Die Grinders", "Electric Die Grinders"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "metal-nibblers-shears": {
    slug: "metal-nibblers-shears",
    title: "Metal Nibblers & Shears",
    singular: "metal nibbler",
    intro:
      "Independent UK reviews and a clear buying guide for metal nibblers & shears — compare top brands on power, build quality and value.",
    metaTitle: `Metal Nibblers & Shears ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK metal nibblers & shears reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-metal-nibblers-shears-uk",
    reviewsHref: "/category/reviews?sub=metal-nibblers-shears",
    articleCategories: ["Metal Nibblers", "Metal Nibblers & Shears"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "planer-thicknessers": {
    slug: "planer-thicknessers",
    title: "Planer Thicknessers",
    singular: "planer thicknesser",
    intro:
      "Independent UK reviews and a clear buying guide for planer thicknessers — compare top brands on power, build quality and value.",
    metaTitle: `Planer Thicknessers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK planer thicknessers reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-planer-thicknessers-uk",
    reviewsHref: "/category/reviews?sub=planer-thicknessers",
    articleCategories: ["Planer Thicknessers", "Planer Thicknessers"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "wet-dry-extractors": {
    slug: "wet-dry-extractors",
    title: "Power Tool Wet/Dry Extractors & Accessories",
    singular: "wet dry extractor",
    intro:
      "Independent UK reviews and a clear buying guide for power tool wet/dry extractors & accessories — compare top brands on power, build quality and value.",
    metaTitle: `Power Tool Wet/Dry Extractors & Accessories ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK power tool wet/dry extractors & accessories reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-wet-dry-extractors-uk",
    reviewsHref: "/category/reviews?sub=wet-dry-extractors",
    articleCategories: ["Wet Dry Extractors", "Power Tool Wet/Dry Extractors & Accessories"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "powered-cutters": {
    slug: "powered-cutters",
    title: "Powered Cutters",
    singular: "powered cutter",
    intro:
      "Independent UK reviews and a clear buying guide for powered cutters — compare top brands on power, build quality and value.",
    metaTitle: `Powered Cutters ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK powered cutters reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-powered-cutters-uk",
    reviewsHref: "/category/reviews?sub=powered-cutters",
    articleCategories: ["Powered Cutters", "Powered Cutters"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "site-radios": {
    slug: "site-radios",
    title: "Site Radios, Speakers & Accessories",
    singular: "site radio",
    intro:
      "Independent UK reviews and a clear buying guide for site radios, speakers & accessories — compare top brands on power, build quality and value.",
    metaTitle: `Site Radios, Speakers & Accessories ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK site radios, speakers & accessories reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-site-radios-uk",
    reviewsHref: "/category/reviews?sub=site-radios",
    articleCategories: ["Site Radios", "Site Radios, Speakers & Accessories"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "wall-chasers": {
    slug: "wall-chasers",
    title: "Wall Chasers & Concrete Cutters",
    singular: "wall chaser",
    intro:
      "Independent UK reviews and a clear buying guide for wall chasers & concrete cutters — compare top brands on power, build quality and value.",
    metaTitle: `Wall Chasers & Concrete Cutters ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK wall chasers & concrete cutters reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-wall-chasers-uk",
    reviewsHref: "/category/reviews?sub=wall-chasers",
    articleCategories: ["Wall Chasers", "Wall Chasers & Concrete Cutters"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "bevellers": {
    slug: "bevellers",
    title: "Bevellers & Accessories",
    singular: "beveller",
    intro:
      "Independent UK reviews and a clear buying guide for bevellers & accessories — compare top brands on power, build quality and value.",
    metaTitle: `Bevellers & Accessories ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK bevellers & accessories reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-bevellers-uk",
    reviewsHref: "/category/reviews?sub=bevellers",
    articleCategories: ["Bevellers", "Bevellers & Accessories"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "powered-caulking-guns": {
    slug: "powered-caulking-guns",
    title: "Powered Caulking Guns",
    singular: "caulking gun",
    intro:
      "Independent UK reviews and a clear buying guide for powered caulking guns — compare top brands on power, build quality and value.",
    metaTitle: `Powered Caulking Guns ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK powered caulking guns reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-powered-caulking-guns-uk",
    reviewsHref: "/category/reviews?sub=powered-caulking-guns",
    articleCategories: ["Caulking Guns", "Powered Caulking Guns"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "rebar-tying-tools": {
    slug: "rebar-tying-tools",
    title: "Rebar Tying Tools",
    singular: "rebar tying tool",
    intro:
      "Independent UK reviews and a clear buying guide for rebar tying tools — compare top brands on power, build quality and value.",
    metaTitle: `Rebar Tying Tools ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK rebar tying tools reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-rebar-tying-tools-uk",
    reviewsHref: "/category/reviews?sub=rebar-tying-tools",
    articleCategories: ["Rebar Tying Tools", "Rebar Tying Tools"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "riveters": {
    slug: "riveters",
    title: "Riveters",
    singular: "riveter",
    intro:
      "Independent UK reviews and a clear buying guide for riveters — compare top brands on power, build quality and value.",
    metaTitle: `Riveters ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK riveters reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-riveters-uk",
    reviewsHref: "/category/reviews?sub=riveters",
    articleCategories: ["Riveters", "Riveters"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "tile-cutters": {
    slug: "tile-cutters",
    title: "Tile Cutters",
    singular: "tile cutter",
    intro:
      "Independent UK reviews and a clear buying guide for tile cutters — compare top brands on power, build quality and value.",
    metaTitle: `Tile Cutters ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK tile cutters reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-tile-cutters-uk",
    reviewsHref: "/category/reviews?sub=tile-cutters",
    articleCategories: ["Tile Cutters", "Tile Cutters"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "angle-grinder-discs": {
    slug: "angle-grinder-discs",
    title: "Angle Grinder Discs",
    singular: "angle grinder disc",
    intro:
      "Independent UK reviews and a clear buying guide for angle grinder discs — compare top brands on power, build quality and value.",
    metaTitle: `Angle Grinder Discs ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK angle grinder discs reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-angle-grinder-discs-uk",
    reviewsHref: "/category/reviews?sub=angle-grinder-discs",
    articleCategories: ["Angle Grinder Discs", "Angle Grinder Discs"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "circular-saw-bushing-washers": {
    slug: "circular-saw-bushing-washers",
    title: "Circular Saw Blade Bushing Washers",
    singular: "bushing washer",
    intro:
      "Independent UK reviews and a clear buying guide for circular saw blade bushing washers — compare top brands on power, build quality and value.",
    metaTitle: `Circular Saw Blade Bushing Washers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK circular saw blade bushing washers reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-circular-saw-bushing-washers-uk",
    reviewsHref: "/category/reviews?sub=circular-saw-bushing-washers",
    articleCategories: ["Bushing Washers", "Circular Saw Blade Bushing Washers"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "electric-planer-blades": {
    slug: "electric-planer-blades",
    title: "Electric Planer Blades",
    singular: "planer blade",
    intro:
      "Independent UK reviews and a clear buying guide for electric planer blades — compare top brands on power, build quality and value.",
    metaTitle: `Electric Planer Blades ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK electric planer blades reviews and a complete buying guide. Top brands compared on power, build quality, features and value.",
    buyingGuideHref: "/best-electric-planer-blades-uk",
    reviewsHref: "/category/reviews?sub=electric-planer-blades",
    articleCategories: ["Planer Blades", "Electric Planer Blades"],
    heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50",
  },
  "garden-multi-tools": {
    slug: "garden-multi-tools",
    title: "Garden Multi-Tools",
    singular: "garden multi-tool",
    intro:
      "One engine, multiple attachments — hedge trimmer, strimmer, chainsaw, brushcutter and pole pruner in a single tool. Read our independent reviews or jump to the buying guide for help choosing the best 5-in-1 multi-tool for your garden.",
    metaTitle: `Garden Multi-Tools ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK garden multi-tool reviews and a complete buying guide. 5-in-1 petrol and cordless multi-tools compared for power, attachments and value.",
    buyingGuideHref: "/best-garden-multi-tools-uk",
    reviewsHref: "/category/reviews?sub=garden-multi-tools",
    articleCategories: ["Garden Multi-Tools", "Multi-Tool", "5-in-1 Multi-Tool"],
    heroGradient: "bg-linear-to-br from-green-50 to-emerald-50",
  },
  "flowers": {
    slug: "flowers",
    title: "Flowers",
    singular: "flower",
    intro:
      "Bring colour, scent and pollinators to your garden with the right flowers. Independent UK reviews and a clear buying guide for bulbs, bedding, perennials and cut flowers.",
    metaTitle: `Flowers ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK flower reviews and a complete buying guide. Bulbs, bedding plants, perennials and cut-flower varieties compared for colour, hardiness and value.",
    buyingGuideHref: "/best-flowers-uk",
    reviewsHref: "/category/reviews?sub=flowers",
    articleCategories: ["Flowers", "Flower"],
    heroGradient: "bg-linear-to-br from-pink-50 to-rose-50",
  },
  "plants": {
    slug: "plants",
    title: "Plants",
    singular: "plant",
    intro:
      "From shrubs and trees to houseplants and herbs — find the right plants for your garden, balcony or windowsill. Independent UK reviews and a clear buying guide below.",
    metaTitle: `Plants ${Y} | UK Reviews & Buying Guide`,
    metaDescription:
      "Independent UK plant reviews and a complete buying guide. Shrubs, trees, perennials, houseplants and herbs compared for hardiness, growth and value.",
    buyingGuideHref: "/best-plants-uk",
    reviewsHref: "/category/reviews?sub=plants",
    articleCategories: ["Plants", "Plant"],
    heroGradient: "bg-linear-to-br from-green-50 to-emerald-50",
  },

  // ============ Auto-generated hubs (bulk import 2026) ============
  "automotive-tools": { slug: "automotive-tools", title: "Automotive Tools", singular: "automotive tool", intro: "Timing kits, torque wrenches, jacks and specialist automotive tools for home mechanics and garages. Independent UK reviews and buying guide below.", metaTitle: `Automotive Tools ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK automotive tool reviews and a complete buying guide. Timing kits, torque wrenches, jacks, axle stands and specialist tools compared.", buyingGuideHref: "/best-automotive-tools-uk", reviewsHref: "/category/reviews?sub=automotive-tools", articleCategories: ["Automotive Tools"], heroGradient: "bg-linear-to-br from-zinc-50 to-slate-50" },
  "workbenches-vices": { slug: "workbenches-vices", title: "Workbenches & Vices", singular: "workbench", intro: "Solid workbenches, engineer's vices and clamps for workshops and garages. Independent UK reviews and buying guide below.", metaTitle: `Workbenches & Vices ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK workbench and vice reviews. Folding, mobile and heavy-duty benches plus engineer's vices and clamps compared.", buyingGuideHref: "/best-workbenches-uk", reviewsHref: "/category/reviews?sub=workbenches-vices", articleCategories: ["Workbenches", "Vices"], heroGradient: "bg-linear-to-br from-stone-50 to-zinc-50" },
  "ladders-steps": { slug: "ladders-steps", title: "Ladders & Steps", singular: "ladder", intro: "Extension ladders, step ladders, combination ladders and platform steps for home and trade. Independent UK reviews and buying guide below.", metaTitle: `Ladders & Steps ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK ladder and step reviews. Extension, combination, telescopic and platform steps compared on reach, weight and EN131 rating.", buyingGuideHref: "/best-ladders-uk", reviewsHref: "/category/reviews?sub=ladders-steps", articleCategories: ["Ladders"], heroGradient: "bg-linear-to-br from-slate-50 to-stone-50" },
  "garden-parasols": { slug: "garden-parasols", title: "Garden Parasols & Umbrellas", singular: "garden parasol", intro: "Cantilever, wall-mounted and centre-pole parasols to keep your patio shaded all summer. Independent UK reviews and buying guide below.", metaTitle: `Garden Parasols ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK garden parasol reviews. Cantilever, half, rectangular and centre-pole umbrellas compared on canopy size, wind resistance and value.", buyingGuideHref: "/best-garden-parasols-uk", reviewsHref: "/category/reviews?sub=garden-parasols", articleCategories: ["Garden Parasols", "Parasols"], heroGradient: "bg-linear-to-br from-amber-50 to-orange-50" },
  "gazebos-marquees": { slug: "gazebos-marquees", title: "Gazebos & Marquees", singular: "gazebo", intro: "Pop-up gazebos, party marquees and event shelters for garden entertaining. Independent UK reviews and buying guide below.", metaTitle: `Gazebos & Marquees ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK gazebo and marquee reviews. Pop-up, hard-top and steel-frame party tents compared on size, wind rating and durability.", buyingGuideHref: "/best-gazebos-uk", reviewsHref: "/category/reviews?sub=gazebos-marquees", articleCategories: ["Gazebos", "Marquees"], heroGradient: "bg-linear-to-br from-teal-50 to-emerald-50" },
  "pet-supplies": { slug: "pet-supplies", title: "Outdoor Pet Supplies", singular: "pet product", intro: "Cat trees, dog crates, pet gates and outdoor play gear for cats and dogs. Independent UK reviews and buying guide below.", metaTitle: `Outdoor Pet Supplies ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK pet supply reviews. Dog crates, cat trees, pet gates, playpens and outdoor pet accessories compared.", buyingGuideHref: "/best-outdoor-pet-supplies-uk", reviewsHref: "/category/reviews?sub=pet-supplies", articleCategories: ["Pet Supplies"], heroGradient: "bg-linear-to-br from-orange-50 to-amber-50" },
  "garden-swings-hammocks": { slug: "garden-swings-hammocks", title: "Garden Swings & Hammocks", singular: "garden swing", intro: "Hammocks, egg chairs, swing seats and nest swings for relaxing outdoors. Independent UK reviews and buying guide below.", metaTitle: `Garden Swings & Hammocks ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK garden swing and hammock reviews. Egg chairs, swing seats, hammocks and nest swings compared on comfort and build.", buyingGuideHref: "/best-garden-swings-uk", reviewsHref: "/category/reviews?sub=garden-swings-hammocks", articleCategories: ["Garden Swings", "Hammocks"], heroGradient: "bg-linear-to-br from-emerald-50 to-teal-50" },
  "garden-storage": { slug: "garden-storage", title: "Garden Storage", singular: "garden storage box", intro: "Storage boxes, deck boxes, storage benches and outdoor cabinets. Independent UK reviews and buying guide below.", metaTitle: `Garden Storage ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK garden storage reviews. Deck boxes, storage benches, cushion boxes and outdoor cabinets compared on capacity and weatherproofing.", buyingGuideHref: "/best-garden-storage-uk", reviewsHref: "/category/reviews?sub=garden-storage", articleCategories: ["Garden Storage"], heroGradient: "bg-linear-to-br from-stone-50 to-amber-50" },
  "bbqs-outdoor-cooking": { slug: "bbqs-outdoor-cooking", title: "BBQs & Outdoor Cooking", singular: "BBQ", intro: "Charcoal, gas and pellet BBQs, smokers and pizza ovens for garden cooking. Independent UK reviews and buying guide below.", metaTitle: `BBQs & Outdoor Cooking ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK BBQ reviews. Charcoal, gas, pellet and kamado BBQs plus smokers and pizza ovens compared on cooking area, heat and features.", buyingGuideHref: "/best-bbqs-uk", reviewsHref: "/category/reviews?sub=bbqs-outdoor-cooking", articleCategories: ["BBQs", "Barbecues"], heroGradient: "bg-linear-to-br from-red-50 to-orange-50" },
  "winches-lifting": { slug: "winches-lifting", title: "Winches & Lifting Gear", singular: "winch", intro: "Electric winches, chain blocks, hoists and lifting equipment for workshop and off-road use. Independent UK reviews and buying guide below.", metaTitle: `Winches & Lifting Gear ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK winch and lifting gear reviews. Electric winches, chain hoists and lifting equipment compared on capacity and duty cycle.", buyingGuideHref: "/best-winches-uk", reviewsHref: "/category/reviews?sub=winches-lifting", articleCategories: ["Winches"], heroGradient: "bg-linear-to-br from-zinc-50 to-slate-50" },
  "safety-workwear": { slug: "safety-workwear", title: "Safety & Workwear", singular: "safety product", intro: "Safety boots, work gloves, hard hats, hi-vis and PPE for site and workshop. Independent UK reviews and buying guide below.", metaTitle: `Safety & Workwear ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK safety and workwear reviews. Boots, gloves, hard hats, hi-vis, ear defenders and respirators compared.", buyingGuideHref: "/best-workwear-uk", reviewsHref: "/category/reviews?sub=safety-workwear", articleCategories: ["Safety", "Workwear"], heroGradient: "bg-linear-to-br from-yellow-50 to-amber-50" },
  "kids-outdoor-play": { slug: "kids-outdoor-play", title: "Kids Outdoor Play", singular: "kids play equipment", intro: "Playhouses, climbing frames, slides, sandpits and swings for kids. Independent UK reviews and buying guide below.", metaTitle: `Kids Outdoor Play ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK kids outdoor play reviews. Playhouses, climbing frames, slides, sandpits and swing sets compared on safety and value.", buyingGuideHref: "/best-kids-outdoor-play-uk", reviewsHref: "/category/reviews?sub=kids-outdoor-play", articleCategories: ["Kids Outdoor Play"], heroGradient: "bg-linear-to-br from-sky-50 to-cyan-50" },
  "planters-pots": { slug: "planters-pots", title: "Planters & Pots", singular: "planter", intro: "Plant pots, planters, window boxes and raised beds for every garden. Independent UK reviews and buying guide below.", metaTitle: `Planters & Pots ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK planter and pot reviews. Terracotta, wooden, metal and self-watering planters compared plus raised beds and window boxes.", buyingGuideHref: "/best-planters-uk", reviewsHref: "/category/reviews?sub=planters-pots", articleCategories: ["Planters", "Pots"], heroGradient: "bg-linear-to-br from-green-50 to-emerald-50" },
  "trampolines": { slug: "trampolines", title: "Trampolines", singular: "trampoline", intro: "Round and rectangular trampolines with enclosures for family gardens. Independent UK reviews and buying guide below.", metaTitle: `Trampolines ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK trampoline reviews. Round, oval and rectangular trampolines with safety nets compared on bounce, safety and durability.", buyingGuideHref: "/best-trampolines-uk", reviewsHref: "/category/reviews?sub=trampolines", articleCategories: ["Trampolines"], heroGradient: "bg-linear-to-br from-lime-50 to-green-50" },
  "garden-sheds": { slug: "garden-sheds", title: "Garden Sheds", singular: "garden shed", intro: "Wooden, metal and plastic garden sheds in every size. Independent UK reviews and buying guide below.", metaTitle: `Garden Sheds ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK garden shed reviews. Apex, pent, wooden, metal and plastic sheds compared on size, security and weatherproofing.", buyingGuideHref: "/best-garden-sheds-uk", reviewsHref: "/category/reviews?sub=garden-sheds", articleCategories: ["Garden Sheds"], heroGradient: "bg-linear-to-br from-amber-50 to-stone-50" },
  "hot-tubs-pools": { slug: "hot-tubs-pools", title: "Hot Tubs & Pools", singular: "hot tub", intro: "Inflatable hot tubs, spas and swimming pools for garden relaxation. Independent UK reviews and buying guide below.", metaTitle: `Hot Tubs & Pools ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK hot tub and pool reviews. Inflatable spas, hot tubs and swimming pools compared on capacity, heating and running cost.", buyingGuideHref: "/best-hot-tubs-uk", reviewsHref: "/category/reviews?sub=hot-tubs-pools", articleCategories: ["Hot Tubs", "Pools"], heroGradient: "bg-linear-to-br from-blue-50 to-cyan-50" },
  "fencing-gates": { slug: "fencing-gates", title: "Fencing & Gates", singular: "fence panel", intro: "Fence panels, posts, trellis and garden gates for boundaries and screening. Independent UK reviews and buying guide below.", metaTitle: `Fencing & Gates ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK fencing and gate reviews. Fence panels, posts, trellis, willow screens and garden gates compared.", buyingGuideHref: "/best-garden-fencing-uk", reviewsHref: "/category/reviews?sub=fencing-gates", articleCategories: ["Fencing", "Gates"], heroGradient: "bg-linear-to-br from-stone-50 to-amber-50" },
  "christmas-decorations": { slug: "christmas-decorations", title: "Christmas Decorations", singular: "Christmas decoration", intro: "Inflatables, lights and outdoor Christmas decorations for the festive season. Independent UK reviews and buying guide below.", metaTitle: `Christmas Decorations ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK Christmas decoration reviews. Inflatables, outdoor lights, trees and displays compared on brightness and durability.", buyingGuideHref: "/best-christmas-decorations-uk", reviewsHref: "/category/reviews?sub=christmas-decorations", articleCategories: ["Christmas Decorations"], heroGradient: "bg-linear-to-br from-red-50 to-green-50" },
  "locks-security": { slug: "locks-security", title: "Locks & Security", singular: "lock", intro: "Padlocks, shed locks, security chains and outdoor security accessories. Independent UK reviews and buying guide below.", metaTitle: `Locks & Security ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK padlock and security reviews. Shed locks, security chains, combination locks and outdoor safes compared.", buyingGuideHref: "/best-padlocks-uk", reviewsHref: "/category/reviews?sub=locks-security", articleCategories: ["Locks", "Security"], heroGradient: "bg-linear-to-br from-slate-50 to-zinc-50" },
  "garden-hand-tools": { slug: "garden-hand-tools", title: "Garden Hand Tools", singular: "garden hand tool", intro: "Hoes, rakes, shears, pruners and hand tools for every gardening task. Independent UK reviews and buying guide below.", metaTitle: `Garden Hand Tools ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK garden hand tool reviews. Hoes, rakes, shears, pruners and hand weeders compared on build quality and value.", buyingGuideHref: "/best-garden-hand-tools-uk", reviewsHref: "/category/reviews?sub=garden-hand-tools", articleCategories: ["Garden Hand Tools"], heroGradient: "bg-linear-to-br from-lime-50 to-emerald-50" },
  "hoses-reels": { slug: "hoses-reels", title: "Garden Hoses & Reels", singular: "garden hose", intro: "Garden hoses, hose reels, connectors and expandable hoses. Independent UK reviews and buying guide below.", metaTitle: `Garden Hoses & Reels ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK garden hose reviews. Hose reels, expandable hoses, fittings and hose accessories compared.", buyingGuideHref: "/best-garden-hoses-uk", reviewsHref: "/category/reviews?sub=hoses-reels", articleCategories: ["Garden Hoses"], heroGradient: "bg-linear-to-br from-cyan-50 to-blue-50" },
  "cleaning-supplies": { slug: "cleaning-supplies", title: "Outdoor Cleaning Supplies", singular: "cleaning tool", intro: "Brooms, mops, scrubbing brushes and outdoor cleaning gear. Independent UK reviews and buying guide below.", metaTitle: `Outdoor Cleaning Supplies ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK outdoor cleaning supply reviews. Brooms, brushes and cleaning tools compared for patios, decking and driveways.", buyingGuideHref: "/best-outdoor-cleaning-uk", reviewsHref: "/category/reviews?sub=cleaning-supplies", articleCategories: ["Cleaning Supplies"], heroGradient: "bg-linear-to-br from-sky-50 to-slate-50" },
  "bird-care": { slug: "bird-care", title: "Bird Care", singular: "bird care product", intro: "Bird feeders, bird tables, baths, boxes and wild bird accessories. Independent UK reviews and buying guide below.", metaTitle: `Bird Care ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK bird care reviews. Bird feeders, tables, baths and nest boxes compared on build and bird-friendliness.", buyingGuideHref: "/best-bird-feeders-uk", reviewsHref: "/category/reviews?sub=bird-care", articleCategories: ["Bird Care"], heroGradient: "bg-linear-to-br from-teal-50 to-cyan-50" },
  "paving-slabs": { slug: "paving-slabs", title: "Paving Slabs & Stones", singular: "paving slab", intro: "Paving slabs, patio stones, stepping stones and block paving. Independent UK reviews and buying guide below.", metaTitle: `Paving Slabs ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK paving slab reviews. Natural stone, porcelain and concrete slabs plus stepping stones compared on finish and price.", buyingGuideHref: "/best-paving-slabs-uk", reviewsHref: "/category/reviews?sub=paving-slabs", articleCategories: ["Paving"], heroGradient: "bg-linear-to-br from-stone-50 to-zinc-50" },
  "sprinklers-irrigation": { slug: "sprinklers-irrigation", title: "Sprinklers & Irrigation", singular: "sprinkler", intro: "Oscillating sprinklers, drip irrigation kits and automated watering systems. Independent UK reviews and buying guide below.", metaTitle: `Sprinklers & Irrigation ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK sprinkler and irrigation reviews. Oscillating, rotary and drip irrigation kits compared on coverage and reliability.", buyingGuideHref: "/best-sprinklers-uk", reviewsHref: "/category/reviews?sub=sprinklers-irrigation", articleCategories: ["Sprinklers", "Irrigation"], heroGradient: "bg-linear-to-br from-cyan-50 to-teal-50" },
  "garden-decor": { slug: "garden-decor", title: "Garden Decor & Ornaments", singular: "garden ornament", intro: "Statues, water features, fountains and decorative garden accessories. Independent UK reviews and buying guide below.", metaTitle: `Garden Decor ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK garden decor reviews. Statues, water features, ornaments and sculptures compared on style and durability.", buyingGuideHref: "/best-garden-decor-uk", reviewsHref: "/category/reviews?sub=garden-decor", articleCategories: ["Garden Decor"], heroGradient: "bg-linear-to-br from-amber-50 to-orange-50" },
  "rugs-outdoor": { slug: "rugs-outdoor", title: "Outdoor Rugs", singular: "outdoor rug", intro: "Weatherproof outdoor rugs for patios, decking and garden rooms. Independent UK reviews and buying guide below.", metaTitle: `Outdoor Rugs ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK outdoor rug reviews. Weatherproof patio rugs, decking rugs and garden mats compared on durability and style.", buyingGuideHref: "/best-outdoor-rugs-uk", reviewsHref: "/category/reviews?sub=rugs-outdoor", articleCategories: ["Outdoor Rugs"], heroGradient: "bg-linear-to-br from-orange-50 to-amber-50" },
  "decking": { slug: "decking", title: "Decking", singular: "decking board", intro: "Composite and timber decking boards, tiles and accessories. Independent UK reviews and buying guide below.", metaTitle: `Decking ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK decking reviews. Composite, hardwood and softwood decking boards and tiles compared on durability and value.", buyingGuideHref: "/best-decking-uk", reviewsHref: "/category/reviews?sub=decking", articleCategories: ["Decking"], heroGradient: "bg-linear-to-br from-stone-50 to-amber-50" },
  "watering-cans": { slug: "watering-cans", title: "Watering Cans", singular: "watering can", intro: "Metal and plastic watering cans for indoor and outdoor use. Independent UK reviews and buying guide below.", metaTitle: `Watering Cans ${Y} | UK Reviews & Buying Guide`, metaDescription: "Independent UK watering can reviews. Metal, plastic and long-reach watering cans compared on capacity and spout precision.", buyingGuideHref: "/best-watering-cans-uk", reviewsHref: "/category/reviews?sub=watering-cans", articleCategories: ["Watering Cans"], heroGradient: "bg-linear-to-br from-emerald-50 to-teal-50" },
};

export const productHubSlugs = Object.keys(productHubs);
